import { z } from 'zod';
import { decimalString } from './common';

/**
 * Schemas for the per-cabinet Drawer line items.
 *
 * Path: `/projects/:projectId/cabinets/:cabinetId/drawers`
 *
 * Drawer is a sub-item of Cabinet. Drawers have their own dimensions,
 * materials, model/type/slide specifications, and pricing.
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const optionalDimMm = z.number().int().min(0).max(100_000).optional().nullable();

const drawerCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);

/**
 * How the drawer's name is decided. POSITION and NUMBER are derived from the
 * drawer's place in the cabinet and are recomputed server-side whenever that
 * changes; see `drawerGeneratedName`.
 */
const drawerNameModeEnum = z.enum(['MANUAL', 'POSITION', 'NUMBER']);
export type DrawerCurrencyValue = z.infer<typeof drawerCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const drawerListItem = z.object({
  id: z.string().cuid(),
  cabinetId: z.string().cuid(),
  catalogItemId: z.string().cuid().nullable(),
  name: z.string().nullable(),
  nameMode: drawerNameModeEnum,
  heightMm: z.number().int().nullable(),
  widthMm: z.number().int().nullable(),
  depthMm: z.number().int().nullable(),
  carcassMaterial: z.string().nullable(),
  frontMaterial: z.string().nullable(),
  drawerModel: z.string().nullable(),
  /**
   * The board(s) as the item tables show them, by the shared rule the quote PDF
   * uses (`formatMaterials`): the saved label when there is one, else the
   * catalog name. Filled by the list endpoints, which load the names; absent
   * on single-item responses. '' when the item carries no material.
   */
  materialDisplay: z.string().optional(),
  drawerType: z.string().nullable(),
  slideModel: z.string().nullable(),
  hBox: z.string().nullable(),
  color: z.string().nullable(),
  quantity: decimalString,
  coefficient: decimalString,
  materialCoefficient: decimalString,
  frontMaterialCoefficient: decimalString,
  systemCoefficient: decimalString,
  unitPriceNet: decimalString.optional(),
  currency: drawerCurrencyEnum.optional(),
  totalNet: decimalString.optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  materialId: z.string().nullable(),
  edgeId: z.string().nullable(),
  frontMaterialId: z.string().nullable(),
  frontEdgeId: z.string().nullable(),
  drawerSystemVariantId: z.string().nullable(),
  hbox: z
    .enum([
      'HBox_70',
      'HBox_85',
      'HBox_106',
      'HBox_146',
      'HBox_186',
      'HBox_226',
      'HBox_266',
      'HBox_306',
    ])
    .nullable(),
  drawerModelKind: z.enum(['BOX', 'INTERIOR', 'STANDARD']).nullable(),
  joinType: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']).nullable(),
  cx2: z.boolean(),
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type DrawerListItem = z.infer<typeof drawerListItem>;

export const drawerDetails = drawerListItem;
export type DrawerDetails = z.infer<typeof drawerDetails>;

export const listDrawersResponse = z.array(drawerListItem);
export type ListDrawersResponse = z.infer<typeof listDrawersResponse>;

// ----- Manual create (no catalog reference) -----
export const createDrawerRequest = z.object({
  name: optionalTrim(200),
  /** Omitted means MANUAL — every existing caller keeps naming by hand. */
  nameMode: drawerNameModeEnum.optional(),
  heightMm: optionalDimMm,
  widthMm: optionalDimMm,
  depthMm: optionalDimMm,
  carcassMaterial: optionalTrim(100),
  frontMaterial: optionalTrim(100),
  drawerModel: optionalTrim(100),
  drawerType: optionalTrim(100),
  slideModel: optionalTrim(100),
  hBox: optionalTrim(50),
  color: optionalTrim(50),
  quantity: positiveDecimal.default('1'),
  coefficient: positiveDecimal.default('1.00'),
  /**
   * Markups for THIS use of each board, seeded from `Material.coefficient`.
   * `materialCoefficient` covers the box (sides, back, bottom); the front is
   * its own board and its own number.
   */
  materialCoefficient: positiveDecimal.optional(),
  frontMaterialCoefficient: positiveDecimal.optional(),
  /**
   * Markup for THIS use of the bought-in slide system, seeded from
   * `DrawerSystemVariant.coefficient`. The system is hardware, not a board, so
   * it carries its own number; omitted, it falls back to `materialCoefficient`,
   * which is what priced it before this field existed.
   */
  systemCoefficient: positiveDecimal.optional(),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: drawerCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  materialId: z.string().cuid().optional().nullable(),
  edgeId: z.string().cuid().optional().nullable(),
  frontMaterialId: z.string().cuid().optional().nullable(),
  frontEdgeId: z.string().cuid().optional().nullable(),
  drawerSystemVariantId: z.string().cuid().optional().nullable(),
  hbox: z
    .enum([
      'HBox_70',
      'HBox_85',
      'HBox_106',
      'HBox_146',
      'HBox_186',
      'HBox_226',
      'HBox_266',
      'HBox_306',
    ])
    .optional()
    .nullable(),
  drawerModelKind: z.enum(['BOX', 'INTERIOR', 'STANDARD']).optional().nullable(),
  joinType: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']).optional().nullable(),
  cx2: z.boolean().optional(),
});
export type CreateDrawerRequest = z.infer<typeof createDrawerRequest>;

// ----- Create from a CatalogItem (snapshot) -----
export const createDrawerFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  unitPriceNet: nonNegativeDecimal,
  currency: drawerCurrencyEnum.optional(),
  quantity: positiveDecimal.default('1'),
  coefficient: positiveDecimal.default('1.00'),
  /**
   * Markups for THIS use of each board, seeded from `Material.coefficient`.
   * `materialCoefficient` covers the box (sides, back, bottom); the front is
   * its own board and its own number.
   */
  materialCoefficient: positiveDecimal.optional(),
  frontMaterialCoefficient: positiveDecimal.optional(),
  /**
   * Markup for THIS use of the bought-in slide system, seeded from
   * `DrawerSystemVariant.coefficient`. The system is hardware, not a board, so
   * it carries its own number; omitted, it falls back to `materialCoefficient`,
   * which is what priced it before this field existed.
   */
  systemCoefficient: positiveDecimal.optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: optionalTrim(200),
      nameMode: drawerNameModeEnum.optional(),
      heightMm: optionalDimMm,
      widthMm: optionalDimMm,
      depthMm: optionalDimMm,
      carcassMaterial: optionalTrim(100),
      frontMaterial: optionalTrim(100),
      drawerModel: optionalTrim(100),
      drawerType: optionalTrim(100),
      slideModel: optionalTrim(100),
      hBox: optionalTrim(50),
      color: optionalTrim(50),
      pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
      materialId: z.string().cuid().optional().nullable(),
      edgeId: z.string().cuid().optional().nullable(),
      frontMaterialId: z.string().cuid().optional().nullable(),
      frontEdgeId: z.string().cuid().optional().nullable(),
      drawerSystemVariantId: z.string().cuid().optional().nullable(),
      hbox: z
        .enum([
          'HBox_70',
          'HBox_85',
          'HBox_106',
          'HBox_146',
          'HBox_186',
          'HBox_226',
          'HBox_266',
          'HBox_306',
        ])
        .optional()
        .nullable(),
      drawerModelKind: z.enum(['BOX', 'INTERIOR', 'STANDARD']).optional().nullable(),
      joinType: z
        .enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE'])
        .optional()
        .nullable(),
      cx2: z.boolean().optional(),
    })
    .optional(),
});
export type CreateDrawerFromCatalogRequest = z.infer<typeof createDrawerFromCatalogRequest>;

// ----- Update (partial) -----
export const updateDrawerRequest = z
  .object({
    name: optionalTrim(200),
    nameMode: drawerNameModeEnum.optional(),
    heightMm: optionalDimMm,
    widthMm: optionalDimMm,
    depthMm: optionalDimMm,
    carcassMaterial: optionalTrim(100),
    frontMaterial: optionalTrim(100),
    drawerModel: optionalTrim(100),
    drawerType: optionalTrim(100),
    slideModel: optionalTrim(100),
    hBox: optionalTrim(50),
    color: optionalTrim(50),
    quantity: positiveDecimal,
    coefficient: positiveDecimal,
    materialCoefficient: positiveDecimal,
    frontMaterialCoefficient: positiveDecimal,
    systemCoefficient: positiveDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: drawerCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    materialId: z.string().cuid().nullable(),
    edgeId: z.string().cuid().nullable(),
    frontMaterialId: z.string().cuid().nullable(),
    frontEdgeId: z.string().cuid().nullable(),
    drawerSystemVariantId: z.string().cuid().nullable(),
    hbox: z
      .enum([
        'HBox_70',
        'HBox_85',
        'HBox_106',
        'HBox_146',
        'HBox_186',
        'HBox_226',
        'HBox_266',
        'HBox_306',
      ])
      .nullable(),
    drawerModelKind: z.enum(['BOX', 'INTERIOR', 'STANDARD']).nullable(),
    joinType: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']).nullable(),
    cx2: z.boolean(),
  })
  .partial();
export type UpdateDrawerRequest = z.infer<typeof updateDrawerRequest>;
